import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import {
  withScriptjs,
  withGoogleMap,
  GoogleMap,
  Marker,
} from 'react-google-maps';
import { trackClientSideError } from '../../Analytics';

const DecoratedMarker = props => {
  const onMarkerClick = evt => {
    props.onMarkerClick(props.id, evt);
  };

  return <Marker onClick={onMarkerClick} {...props} />;
};

DecoratedMarker.propTypes = {
  onMarkerClick: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

DecoratedMarker.defaultProps = {
  onMarkerClick: Function.prototype,
};

class Location extends PureComponent {
  constructor(props) {
    super(props);

    this.map = React.createRef();
  }
  static propTypes = {
    zoom: PropTypes.number.isRequired,
    center: PropTypes.shape({
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }).isRequired,
    markerLocations: PropTypes.arrayOf(
      PropTypes.shape({
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        markerIcon: PropTypes.shape({
          url: PropTypes.string.isRequired,
          width: PropTypes.number.isRequired,
          height: PropTypes.number.isRequired,
        }),
      })
    ).isRequired,
    satelliteView: PropTypes.bool,
    fullscreenView: PropTypes.bool,
    streetView: PropTypes.bool,
    zoomView: PropTypes.bool,
    scaleView: PropTypes.bool,
    fitBounds: PropTypes.bool,
    onMarkerClick: PropTypes.func,
  };
  static defaultProps = {
    satelliteView: false,
    fullscreenView: false,
    streetView: false,
    zoomView: false,
    scaleView: false,
    fitBounds: false,
    onMarkerClick: Function.prototype,
  };

  fitBounds() {
    const { fitBounds, markerLocations } = this.props;
    if (fitBounds) {
      const bounds = new window.google.maps.LatLngBounds();
      markerLocations.forEach(location => {
        const { lat, lng } = location;
        bounds.extend(new window.google.maps.LatLng(lat, lng));
      });

      this.map.current.fitBounds(bounds);
    }
  }

  componentDidMount() {
    let { markerLocations } = this.props;
    if (!markerLocations || markerLocations.length === 0) {
      trackClientSideError('There were no properties matching your selections');
    }
    this.fitBounds();
  }

  componentDidUpdate() {
    this.fitBounds();
  }

  render() {
    const { zoom, center, markerLocations, onMarkerClick } = this.props;
    return (
      <GoogleMap
        ref={this.map}
        zoom={zoom}
        center={center}
        defaultOptions={{
          mapTypeControl: this.props.satelliteView,
          fullscreenControl: this.props.fullscreenView,
          streetViewControl: this.props.streetView,
          zoomControl: this.props.zoomView,
          scaleControl: this.props.scaleView,
        }}
      >
        {markerLocations.map(({ lat, lng, id, markerIcon }, i) => {
          const icon = markerIcon
            ? {
                url: markerIcon.url,
                scaledSize: new window.google.maps.Size(
                  markerIcon.width,
                  markerIcon.height
                ),
              }
            : undefined;
          return (
            <DecoratedMarker
              onMarkerClick={onMarkerClick}
              icon={icon}
              id={id}
              key={i}
              position={{ lat, lng }}
            />
          );
        })}
      </GoogleMap>
    );
  }
}

const EnhancedLocation = withScriptjs(withGoogleMap(Location));

/** @visibleName Location Map */
export default class LocationMap extends PureComponent {
  constructor(props) {
    super(props);

    this.getMapContainer = this.getMapContainer.bind(this);
    this.getLoadingContainer = this.getLoadingContainer.bind(this);

    this.googleMapsApiLink = this.props.googleMapsApiKey
      ? `https://maps.googleapis.com/maps/api/js?key=${this.props.googleMapsApiKey}&v=3.exp&libraries=geometry,drawing,places`
      : `https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=geometry,drawing,places`;
  }

  static propTypes = {
    /** The google maps api key, while it is optional, it should nevertheless be added */
    googleMapsApiKey: PropTypes.string,
    /** Optional loading element before the map is shown, it should have the same dimensions as the map */
    loading: PropTypes.element,
    /** Optional dimensions for the map; by default it takes the size of its container */
    dimensions: PropTypes.shape({
      width: PropTypes.string,
      height: PropTypes.string,
    }),
    /** How much zoom should the map have when it is first loaded */
    zoom: PropTypes.number.isRequired,
    /** Where the center of the map should be, it can be the same as the marker so the marker is in the center */
    center: PropTypes.shape({
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }).isRequired,
    /** The location on the map where the marker is placed */
    markerLocations: PropTypes.arrayOf(
      PropTypes.shape({
        latitude: PropTypes.number,
        longitude: PropTypes.number,
      })
    ).isRequired,
    /** Enable or disable the satellite button toggle */
    satelliteView: PropTypes.bool,
    /** Enable or disable fullscreen button toggle */
    fullscreenView: PropTypes.bool,
    /** Enable or disable street view button toggle */
    streetView: PropTypes.bool,
    /** Enable or disable zoom view button toggle */
    zoomView: PropTypes.bool,
    /** Enable or disable map scale view on the bottom of the map */
    scaleView: PropTypes.bool,
  };
  static defaultProps = {
    googleMapsApiKey: null,
    loading: null,
    satelliteView: false,
    fullscreenView: false,
    streetView: false,
    zoomView: false,
    scaleView: false,
  };

  //The api for the library also requires a loading container while the map is being fetched in the form
  //of an element, if one is not supplied it will just take the properties of the supplied dimensions
  getLoadingContainer() {
    if (this.props.loading) {
      return this.props.loading;
    } else if (this.props.dimensions != null) {
      return (
        <div
          style={{
            width: this.props.dimensions.width,
            height: this.props.dimensions.height,
          }}
        />
      );
    } else {
      return <div style={{ width: '100%', height: '100%' }} />;
    }
  }

  //We must specify a dimension for the container and the map, by default it take the space of its container
  getMapContainer() {
    if (this.props.dimensions == null) {
      return <div style={{ width: '100%', height: '100%', margin: 'auto' }} />;
    } else {
      return (
        <div
          style={{
            width: this.props.dimensions.width,
            height: this.props.dimensions.height,
            margin: 'auto',
          }}
        />
      );
    }
  }

  render() {
    return (
      <EnhancedLocation
        googleMapURL={this.googleMapsApiLink}
        containerElement={this.getMapContainer()}
        mapElement={this.getMapContainer()}
        loadingElement={this.getLoadingContainer()}
        {...this.props}
      />
    );
  }
}
